import type {
  CLSMetric,
  FCPMetric,
  INPMetric,
  LCPMetric,
  TTFBMetric,
} from "web-vitals";
import type { WebVitalsConfig, WebVitalMetric, Logger } from "./types";

export class WebVitalsTracker {
  private config: WebVitalsConfig;
  private logger?: Logger;
  private isInitialized = false;

  constructor(config: WebVitalsConfig, logger?: Logger) {
    this.config = config;
    this.logger = logger;
  }

  async initialize(): Promise<void> {
    if (!this.config.enabled || this.isInitialized) {
      return;
    }

    try {
      const webVitals = await import("web-vitals");
      const { onCLS, onINP, onFCP, onLCP, onTTFB } = webVitals;

      // Setup metric handlers
      onCLS((metric) => this.handleMetric("CLS", metric));
      onINP((metric) => this.handleMetric("INP", metric));
      onFCP((metric) => this.handleMetric("FCP", metric));
      onLCP((metric) => this.handleMetric("LCP", metric));
      onTTFB((metric) => this.handleMetric("TTFB", metric));

      this.isInitialized = true;

      this.logger?.info("Web Vitals tracking initialized", {
        enabled: this.config.enabled,
        sampleRate: this.config.sampleRate,
      });
    } catch (error) {
      this.logger?.error("Failed to initialize Web Vitals tracking", { error });
      this.config.onError?.(error as Error);
    }
  }

  private handleMetric(
    name: WebVitalMetric["name"],
    metric: CLSMetric | INPMetric | FCPMetric | LCPMetric | TTFBMetric,
  ): void {
    if (Math.random() > this.config.sampleRate) {
      return;
    }

    const webVitalMetric: WebVitalMetric = {
      name,
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta,
      id: metric.id,
      timestamp: Date.now(),
      page: typeof window !== "undefined" ? window.location.pathname : "",
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    };

    // Check against thresholds
    const threshold = this.config.reportThresholds?.[name];
    if (threshold && metric.value > threshold) {
      this.logger?.warn(`Web Vitals threshold exceeded`, {
        metric: webVitalMetric,
        threshold,
      });
    }

    // Report metric
    this.config.onMetric?.(webVitalMetric);
    this.logger?.debug(`Web Vitals metric collected`, {
      metric: webVitalMetric,
    });
  }

  getMetrics(): WebVitalMetric[] {
    // Return stored metrics if needed
    return [];
  }

  destroy(): void {
    this.isInitialized = false;
  }
}
